import React, { useState } from "react";
import { ChevronDown, ChevronRight, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { OrgUnitTree } from "@/components/org-unit/OrgUnitTree";
import type { OrgUnitHierarchy, OrgUnitOption } from "@/components/org-unit/OrgUnitTree";
import type { MonthOption } from "../types";
import { useLanguage } from "@/i18n/LanguageContext";
import { SelectField, RunButton } from "./FormFields";

export const AnomalySidebar: React.FC<{
  publicMode?: boolean;
  indicator: string;
  onIndicatorChange: (v: string) => void;
  indicatorOptions: string[];
  year: string;
  month: string;
  onYearChange: (v: string) => void;
  onMonthChange: (v: string) => void;
  yearOptions: string[];
  monthOptions: MonthOption[];
  hierarchy: OrgUnitHierarchy;
  onHierarchyChange: (v: OrgUnitHierarchy) => void;
  orgUnitOptions: OrgUnitOption[];
  loading: boolean;
  onRun: () => void;
}> = React.memo(({ publicMode = false, indicator, onIndicatorChange, indicatorOptions, hierarchy, onHierarchyChange, orgUnitOptions, loading, onRun }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [indicatorOpen, setIndicatorOpen] = useState(true);
  const [orgUnitOpen, setOrgUnitOpen] = useState(true);

  return (
    <aside className="relative space-y-2 lg:sticky lg:top-1 lg:self-start">
      <button
        type="button"
        onClick={() => navigate(publicMode ? "/embed/anomalies/guide" : "/anomalies/guide")}
        className="group flex w-full items-center justify-between rounded-2xl border border-cyan-200/70 bg-gradient-to-r from-cyan-50 to-white px-3 py-2.5 text-left shadow-sm transition hover:border-cyan-300 hover:shadow-md"
      >
        <span>
          <span className="block text-[10px] font-bold uppercase tracking-[0.12em] text-cyan-700">{t("dash.sidebar.explore")}</span>
          <span className="mt-0.5 block text-xs font-semibold text-slate-800">{t("dash.sidebar.exploreTitle")}</span>
        </span>
        <HelpCircle className="h-4 w-4 shrink-0 text-cyan-600 transition-transform group-hover:scale-110" />
      </button>
      <section className="card p-2.5">
        <div
          className="flex cursor-pointer items-center justify-between select-none"
          onClick={() => setIndicatorOpen((p) => !p)}
        >
          <h3 className="text-[10px] font-medium text-slate-500">{t("dash.sidebar.indicator")}</h3>
          {indicatorOpen ? (
            <ChevronDown className="h-3 w-3 text-slate-400" />
          ) : (
            <ChevronRight className="h-3 w-3 text-slate-400" />
          )}
        </div>
        {indicatorOpen && (
          <div className="mt-1.5">
            <SelectField
              searchable
              label=""
              value={indicator}
              onChange={onIndicatorChange}
              width="w-full"
              options={indicatorOptions}
              placeholder={t("dash.sidebar.selectIndicator")}
            />
          </div>
        )}
      </section>

      <section className="card p-2.5">
        <div
          className="flex cursor-pointer items-center justify-between select-none"
          onClick={() => setOrgUnitOpen((p) => !p)}
        >
          <h3 className="text-[10px] font-medium text-slate-500" title={t("dash.sidebar.orgUnitHint")}>{t("dash.sidebar.orgUnit")}</h3>
          {orgUnitOpen ? (
            <ChevronDown className="h-3 w-3 text-slate-400" />
          ) : (
            <ChevronRight className="h-3 w-3 text-slate-400" />
          )}
        </div>
        {orgUnitOpen && (
          <div className="mt-1.5">
            <OrgUnitTree
              orgUnits={orgUnitOptions}
              selected={hierarchy}
              onSelect={onHierarchyChange}
            />
          </div>
        )}
      </section>

      <RunButton loading={loading} onClick={onRun} />
    </aside>
  );
});
AnomalySidebar.displayName = "AnomalySidebar";
